import clsx from "clsx";
import { FC, Fragment, ReactNode } from "react";

import {
  EvalSample,
  ModelFallback,
  ProvenanceData,
} from "@tsmono/inspect-common/types";
import { inputString } from "@tsmono/inspect-common/utils";
import { RenderedText } from "@tsmono/inspect-components/content";
import { arrayToString } from "@tsmono/util";

import {
  EvalSampleTarget,
  EvalSampleWorkingTime,
} from "../../@types/extraInspect";
import { SampleSummary } from "../../client/api/types";
import { kScoreTypeOther } from "../../constants";
import {
  resolveScorePanelView,
  useEvalScorePanelView,
  useSampleDescriptor,
  useScorePanelView,
  useSelectedLogDetails,
  useSelectedScores,
} from "../../state/hooks";
import { formatModelText } from "../../utils/evalModel";
import { formatDateTime, formatTime } from "../../utils/format";

import { SamplesDescriptor } from "./descriptor/samplesDescriptor";
import { SampleErrorView } from "./error/SampleErrorView";
import { ScorePanel } from "./header-v2/ScorePanel";
import { ScoreValueDisplay } from "./header-v2/ScoreValueDisplay";
import styles from "./SampleSummaryView.module.css";
import { isCancelled } from "./status/status";

// Generous truncation for Input/Answer so the value can actually fill
// the available width before the CSS line-clamp kicks in. Target stays
// shorter — it's almost always one or two words anyway.
const kBodyTruncate = 360;
const kTargetTruncate = 160;

interface SampleSummaryViewProps {
  parent_id: string;
  sample: SampleSummary | EvalSample;
  /**
   * Render only the meta line (id · task · epoch · …). The fields row,
   * score panel, and error block are all hidden. The parent decides
   * when to flip this — typically driven by scroll/UI state.
   */
  collapsed?: boolean;
}

interface SampleFields {
  id: string | number;
  epoch: number;
  input: string[];
  target: EvalSampleTarget;
  answer?: string;
  limit?: string;
  limit_reason?: string;
  retries?: number | null;
  model_fallbacks?: ModelFallback[] | null;
  working_time?: EvalSampleWorkingTime;
  total_time?: EvalSample["total_time"];
  error?: string;
  cancelled?: boolean;
}

function isEvalSample(
  sample: SampleSummary | EvalSample
): sample is EvalSample {
  return "store" in sample;
}

const resolveSample = (
  sample: SampleSummary | EvalSample,
  sampleDescriptor: SamplesDescriptor
): SampleFields => {
  const input = inputString(sample.input);
  if (isEvalSample(sample) && sample.choices && sample.choices.length > 0) {
    input.push("");
    input.push(
      ...sample.choices.map((choice, index) => {
        return `${String.fromCharCode(65 + index)}) ${choice}`;
      })
    );
  }

  const target = sample.target;
  const answer =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    sample && sampleDescriptor
      ? sampleDescriptor.selectedScorerDescriptor(sample)?.answer()
      : undefined;
  // the sole caller passes a SampleSummary (useSelectedSampleSummary), so the
  // summary branch is the one that renders — reading only the EvalSample side
  // left the whole limit item dead
  const limit = isEvalSample(sample)
    ? sample.limit?.type
    : (sample.limit ?? undefined);
  const limit_reason = isEvalSample(sample)
    ? (sample.limit?.reason ?? undefined)
    : (sample.limit_reason ?? undefined);
  const working_time = isEvalSample(sample) ? sample.working_time : undefined;
  const total_time = isEvalSample(sample) ? sample.total_time : undefined;
  const cancelled = isCancelled(sample);
  const error = isEvalSample(sample) ? sample.error?.message : undefined;
  const retries = isEvalSample(sample)
    ? sample.error_retries?.length
    : sample.retries;

  return {
    id: sample.id,
    epoch: sample.epoch,
    input,
    target,
    answer,
    limit,
    limit_reason,
    retries,
    model_fallbacks: sample.model_fallbacks,
    working_time,
    total_time,
    error,
    cancelled,
  };
};

interface MetaItem {
  key: string;
  content: ReactNode;
  title?: string;
}

const MetaLine: FC<{ items: MetaItem[] }> = ({ items }) => (
  <div className={clsx(styles.metaLine, "text-size-smaller")}>
    {items.map((item, idx) => (
      <Fragment key={item.key}>
        {idx > 0 && <span className={styles.metaSep}>·</span>}
        <span title={item.title}>{item.content}</span>
      </Fragment>
    ))}
  </div>
);

const FieldLabel: FC<{ children: ReactNode }> = ({ children }) => (
  <div className={clsx(styles.fieldLabel)} data-unsearchable={true}>
    {children}
  </div>
);

/**
 * The single-sample header that sits above the transcript / messages /
 * scores tabs. Renders sample id and meta on a single row, with
 * Input / Target / Answer beneath, and a right-hand score panel.
 *
 * The score panel is currently a simple stack of label-over-value
 * pairs; the next stage of the V2 redesign replaces this with a
 * dedicated `ScorePanel` (chips / sortable grid + view toggle).
 */
export const SampleSummaryView: FC<SampleSummaryViewProps> = ({
  parent_id,
  sample,
  collapsed = false,
}) => {
  const sampleDescriptor = useSampleDescriptor();
  const selectedScores = useSelectedScores();
  const selectedLogDetails = useSelectedLogDetails();
  const taskName = selectedLogDetails?.eval.task;
  const modelText = formatModelText(selectedLogDetails?.eval);
  const [storedScoreView] = useScorePanelView();
  const evalScoreView = useEvalScorePanelView();
  if (!sampleDescriptor) {
    return undefined;
  }
  const fields = resolveSample(sample, sampleDescriptor);

  // Filter out scores whose descriptor renders empty — they shouldn't
  // contribute to the count or layout decisions.
  const visibleScores = selectedScores.filter((scoreLabel) => {
    const rendered = sampleDescriptor.evalDescriptor
      .score(sample, scoreLabel)
      ?.render();
    return rendered !== undefined && rendered !== "";
  });
  const scoreCount = visibleScores.length;

  // Two-column grid widens the right side once the score panel needs
  // room (3+ scores).
  const wideRight = scoreCount >= 3;
  const compactScores = scoreCount > 0 && scoreCount <= 2;

  const metaItems: MetaItem[] = [
    {
      key: "id",
      content: <span className={styles.metaId}>{String(fields.id)}</span>,
    },
    { key: "epoch", content: `Epoch ${fields.epoch}` },
  ];
  if (taskName) {
    metaItems.push({ key: "task", content: taskName });
  }
  const fallbackModels = [
    ...new Set((fields.model_fallbacks ?? []).map((f) => f.fallback_model)),
  ];
  if (modelText || fallbackModels.length > 0) {
    metaItems.push({
      key: "model",
      content: (
        <>
          {modelText}
          {fallbackModels.length > 0 && (
            <em>{` (fallback → ${fallbackModels.join(", ")})`}</em>
          )}
        </>
      ),
      title:
        fallbackModels.length > 0
          ? "Requests refused by the requested model were served by a fallback model"
          : undefined,
    });
  }
  if (fields.total_time) {
    metaItems.push({
      key: "time",
      content: formatTime(fields.total_time),
      title:
        fields.working_time !== undefined && fields.working_time !== null
          ? `Working time: ${formatTime(fields.working_time)}`
          : undefined,
    });
  }
  if (fields.limit) {
    metaItems.push({
      key: "limit",
      content: `Limit: ${fields.limit}`,
      title: fields.limit_reason,
    });
  }
  if (
    fields.retries !== undefined &&
    fields.retries !== null &&
    fields.retries > 0
  ) {
    metaItems.push({ key: "retries", content: `Retries: ${fields.retries}` });
  }
  if (fields.cancelled) {
    metaItems.push({ key: "cancelled", content: "Cancelled" });
  }

  // Check if sample is invalidated (only available on full EvalSample)
  const invalidation: ProvenanceData | null | undefined = isEvalSample(sample)
    ? sample.invalidation
    : undefined;

  if (collapsed) {
    return (
      <div id={`sample-heading-${parent_id}`} className={styles.root}>
        {invalidation && <InvalidationBanner invalidation={invalidation} />}
        <div className={styles.collapsedMeta}>
          <MetaLine items={metaItems} />
        </div>
      </div>
    );
  }

  // Only emit grid tracks for fields that actually render so a sample
  // with no target/answer doesn't reserve empty columns. When 1–2
  // scores are present we fold them into this grid as additional
  // peer columns instead of a separate right column — the compact
  // case visually reads like "extra fields" rather than a panel.
  const fieldTracks: string[] = ["minmax(0, 1fr)"]; // Input always
  if (fields.target) fieldTracks.push("minmax(60px, auto)");
  if (fields.answer) fieldTracks.push("minmax(0, 1fr)");
  if (compactScores) {
    for (let i = 0; i < scoreCount; i++) fieldTracks.push("auto");
  }
  const gridTemplateColumns = fieldTracks.join(" ");

  // Right column is only used by the panel layout (3+ scores).
  const showRight = scoreCount > 0 && !compactScores;

  // Right column max width depends on the active score-panel view.
  // - Chips view: aim for ~3 chips per row by sizing to the actual
  //   scorer-name lengths. Chip ≈ chrome (padding + border + value
  //   glyph + gap, ~41px) + name text (~6.5px/char at 0.8rem). Cap
  //   per-chip at the CSS `max-width: 220px`; cap the column at 560px
  //   so Input still gets breathable room.
  // - Grid view: rows are name + value, so a narrower column is
  //   plenty. Use a fixed 320px max regardless of score count.
  const scorePanelView = resolveScorePanelView(
    storedScoreView,
    evalScoreView,
    scoreCount
  );
  const dynamicRightMax = showRight
    ? scorePanelView === "grid"
      ? 320
      : (() => {
          const avgNameLen =
            visibleScores.reduce((acc, s) => acc + s.name.length, 0) /
            scoreCount;
          const cappedChipPx = Math.min(220, 41 + avgNameLen * 6.5);
          const target = Math.round(3 * cappedChipPx + 2 * 4 + 24);
          return Math.max(220, Math.min(560, target));
        })()
    : null;

  return (
    <div id={`sample-heading-${parent_id}`} className={styles.root}>
      {invalidation && <InvalidationBanner invalidation={invalidation} />}
      <div
        className={clsx(
          styles.layout,
          wideRight && styles.wideRight,
          !showRight && styles.noRight
        )}
        style={
          dynamicRightMax !== null
            ? {
                // Cap the right column at 40% of container so the score
                // panel never dominates the width on narrow viewports.
                // The panel is horizontally scrollable inside, so it can
                // tolerate a tighter cell.
                gridTemplateColumns: `minmax(0, 1fr) min(${dynamicRightMax}px, 40%)`,
              }
            : undefined
        }
      >
        <div className={styles.left}>
          <MetaLine items={metaItems} />
          <div className={styles.fields} style={{ gridTemplateColumns }}>
            <div className={styles.field}>
              <FieldLabel>Input</FieldLabel>
              <div className={clsx(styles.fieldValue, styles.clamp)}>
                <RenderedText
                  markdown={fields.input.join(" ")}
                  truncateAt={kBodyTruncate}
                />
              </div>
            </div>
            {fields.target ? (
              <div className={clsx(styles.field, styles.fieldTarget)}>
                <FieldLabel>Target</FieldLabel>
                <div className={clsx(styles.fieldValue, styles.clamp)}>
                  <RenderedText
                    markdown={arrayToString(fields.target || "none")}
                    truncateAt={kTargetTruncate}
                    className={clsx("no-last-para-padding")}
                  />
                </div>
              </div>
            ) : null}
            {fields.answer ? (
              <div className={styles.field}>
                <FieldLabel>Answer</FieldLabel>
                <div className={clsx(styles.fieldValue, styles.clamp)}>
                  <RenderedText
                    markdown={fields.answer || ""}
                    truncateAt={kBodyTruncate}
                    className={clsx("no-last-para-padding")}
                  />
                </div>
              </div>
            ) : null}
            {compactScores
              ? visibleScores.map((scoreLabel) => {
                  const selected = sampleDescriptor.evalDescriptor.score(
                    sample,
                    scoreLabel
                  );
                  const desc =
                    sampleDescriptor.evalDescriptor.scoreDescriptor(scoreLabel);
                  const label =
                    visibleScores.length === 1 ? "Score" : scoreLabel.name;
                  return (
                    <div
                      key={`${scoreLabel.scorer}-${scoreLabel.name}`}
                      className={clsx(styles.field, styles.scoreField)}
                      title={scoreLabel.name}
                    >
                      <FieldLabel>{label}</FieldLabel>
                      <div className={styles.scoreFieldValue}>
                        <ScoreValueDisplay
                          value={selected?.value}
                          scoreType={desc?.scoreType ?? kScoreTypeOther}
                          size={22}
                        />
                      </div>
                    </div>
                  );
                })
              : null}
          </div>
        </div>
        {showRight ? (
          <div className={styles.right}>
            <ScorePanel
              scores={visibleScores}
              sample={sample}
              evalDescriptor={sampleDescriptor.evalDescriptor}
            />
          </div>
        ) : null}
      </div>
      {fields.error ? (
        <div className={styles.errorBlock}>
          <SampleErrorView message={fields.error} />
        </div>
      ) : null}
    </div>
  );
};

/**
 * Banner component to display when a sample has been invalidated.
 */
const InvalidationBanner: FC<{ invalidation: ProvenanceData }> = ({
  invalidation,
}) => {
  const formatTimestamp = (timestamp: string) => {
    try {
      return formatDateTime(new Date(timestamp));
    } catch {
      return timestamp;
    }
  };

  return (
    <div className={styles.invalidationBanner}>
      <div className={styles.invalidationIcon}>⚠</div>
      <div className={styles.invalidationContent}>
        <div className={styles.invalidationTitle}>Sample Invalidated</div>
        <div className={styles.invalidationDetails}>
          {invalidation.author && <span>By: {invalidation.author}</span>}
          {invalidation.timestamp && (
            <span>On: {formatTimestamp(invalidation.timestamp)}</span>
          )}
          {invalidation.reason && (
            <span className={styles.invalidationReason}>
              Reason: {invalidation.reason}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
